/**
 * What the ⌘K palette shows from a "Search or ask" answer (CLA-1813).
 *
 * The server (claro-elixir CLA-1805, contract on CLA-1795) ranks a query into
 * cards. This turns one answer into palette rows, applying the contract's
 * display rule and removing what the palette's own live search already lists:
 *
 * - `top_p` ≥ 0.7: the top result only, and it is the default Enter target.
 * - 0.4 to 0.7: the top three results.
 * - below 0.4: help answers only, if any. When the answer is final and nothing
 *   else in the palette matched either, the palette says "Nothing found" and
 *   offers "Send feedback" with the query (`showNothingFound`).
 *
 * Patients and notes come back from the resolver through the same scoped
 * searches the palette already runs, so a row whose `ref` is already listed is
 * dropped: the existing row stays where it is.
 *
 * Unknown card types are skipped, and a `navigate` card with no `web_path` (a
 * screen the web has no page for) is skipped too: on the web it would be a
 * row that goes nowhere.
 */
import type { AssistResolveResponse, AssistResult } from '@/api/types'

export const ASSIST_CONFIDENT_P = 0.7
export const ASSIST_CHIPS_P = 0.4

export type AssistMode = 'confident' | 'chips' | 'low'

export function assistMode(topP: number): AssistMode {
  if (topP >= ASSIST_CONFIDENT_P) return 'confident'
  if (topP >= ASSIST_CHIPS_P) return 'chips'
  return 'low'
}

export type AssistRow =
  | { kind: 'help'; id: string; slug: string; title: string; answer: string | null; webPath: string | null }
  | { kind: 'navigate'; id: string; title: string; subtitle: string | null; webPath: string }
  | { kind: 'patient'; id: string; ref: string; name: string; subtitle: string | null }
  | { kind: 'note'; id: string; ref: string; title: string; subtitle: string | null }

export interface AssistShown {
  mode: AssistMode
  rows: AssistRow[]
  /** The row Enter should go to before the user moves the selection, if any. */
  defaultRowId: string | null
  /** Say "Nothing found" and offer feedback (the caller adds "nothing local matched"). */
  showNothingFound: boolean
}

export interface AlreadyListed {
  /** Patient UUIDs the palette's live search already shows. */
  patientRefs: ReadonlySet<string>
  /** Note UUIDs the palette's live search already shows. */
  noteRefs: ReadonlySet<string>
  /** Paths of the navigation commands the palette already shows. */
  paths: ReadonlySet<string>
}

/** The results the display rule lets through, best first. */
export function pickResults(response: AssistResolveResponse): AssistResult[] {
  const mode = assistMode(response.top_p)
  if (mode === 'confident') return response.results.slice(0, 1)
  if (mode === 'chips') return response.results.slice(0, 3)
  return response.results.filter((r) => r.card.type === 'help').slice(0, 3)
}

/** One result's card as palette rows (several for a patients or notes card). */
function rowsFor(result: AssistResult, index: number, listed: AlreadyListed): AssistRow[] {
  const card = result.card
  switch (card.type) {
    case 'help': {
      if (!card.slug || !card.title) return []
      return [{
        kind: 'help',
        id: `assist-help-${card.slug}`,
        slug: card.slug,
        title: card.title,
        answer: card.answer ?? null,
        webPath: card.target?.web_path ?? null,
      }]
    }
    case 'navigate': {
      const webPath = card.target?.web_path
      if (!webPath || !card.title || listed.paths.has(webPath)) return []
      return [{
        kind: 'navigate',
        id: `assist-nav-${index}-${webPath}`,
        title: card.title,
        subtitle: card.subtitle ?? null,
        webPath,
      }]
    }
    case 'patients':
      return (card.items ?? [])
        .filter((it) => it.ref && !listed.patientRefs.has(it.ref))
        .map((it) => ({
          kind: 'patient' as const,
          id: `assist-patient-${it.ref}`,
          ref: it.ref,
          name: it.name || it.title || 'Patient',
          subtitle: it.subtitle ?? null,
        }))
    case 'notes':
      return (card.items ?? [])
        .filter((it) => it.ref && !listed.noteRefs.has(it.ref))
        .map((it) => ({
          kind: 'note' as const,
          id: `assist-note-${it.ref}`,
          ref: it.ref,
          title: it.title || it.name || 'Note',
          subtitle: it.subtitle ?? null,
        }))
    default:
      return []
  }
}

/**
 * What to show for `response`. `final` is whether it answered a final query
 * (Enter or a pause): "Nothing found" waits for one, so it does not flash up
 * between keystrokes.
 */
export function assistShown(
  response: AssistResolveResponse,
  final: boolean,
  listed: AlreadyListed,
): AssistShown {
  const mode = assistMode(response.top_p)
  const picked = pickResults(response)

  const rows: AssistRow[] = []
  const seen = new Set<string>()
  let topRowId: string | null = null
  picked.forEach((result, i) => {
    for (const row of rowsFor(result, i, listed)) {
      if (seen.has(row.id)) continue
      seen.add(row.id)
      rows.push(row)
      if (i === 0 && topRowId === null) topRowId = row.id
    }
  })

  return {
    mode,
    rows,
    defaultRowId: mode === 'confident' ? topRowId : null,
    showNothingFound: mode === 'low' && final && rows.length === 0,
  }
}
